import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Phone, ArrowRight, Stethoscope, Baby, HeartPulse, Brain, Venus, Activity, ShieldCheck, Clock, CalendarCheck, AlertTriangle } from "lucide-react";
import AIConciergeWidget from "@/components/AIConciergeWidget";
import DemoTopNav from "@/components/demos/DemoTopNav";
import { DEMO_THEMES } from "@/data/demoThemes";

const BLUE = "#2563EB";
const CORAL = "#F97362";
const INK = "#0B2447";
const body = { fontFamily: "'Plus Jakarta Sans', sans-serif" };

const IMG = {
  hero: "https://images.unsplash.com/photo-1648775507324-b48dd3791fa5?crop=entropy&cs=srgb&fm=jpg&w=1100&q=80&ixlib=rb-4.1.0",
  d1: "https://images.unsplash.com/photo-1559839734-2b71ea197ec2?crop=entropy&cs=srgb&fm=jpg&w=500&q=80&ixlib=rb-4.1.0",
  d2: "https://images.unsplash.com/photo-1612531385446-f7e6d131e1d0?crop=entropy&cs=srgb&fm=jpg&w=500&q=80&ixlib=rb-4.1.0",
  d3: "https://images.unsplash.com/photo-1659353888906-adb3e0041693?crop=entropy&cs=srgb&fm=jpg&w=500&q=80&ixlib=rb-4.1.0",
  d4: "https://images.unsplash.com/photo-1673865641073-4479f93a7776?crop=entropy&cs=srgb&fm=jpg&w=500&q=80&ixlib=rb-4.1.0",
};
const SERVICES = [
  { icon: Stethoscope, name: "Primary Care", desc: "Everyday health, check-ups and prevention." },
  { icon: Baby, name: "Pediatrics", desc: "Gentle, expert care for children of all ages." },
  { icon: Venus, name: "Women's Health", desc: "Comprehensive care through every life stage." },
  { icon: HeartPulse, name: "Cardiology", desc: "Heart health screening and management." },
  { icon: Brain, name: "Behavioral Health", desc: "Compassionate mental & emotional support." },
  { icon: Activity, name: "Urgent Visits", desc: "Same-day care when you need it most." },
];
const PROVIDERS = [
  { img: IMG.d1, name: "Dr. Emily Chen", spec: "Family Medicine" },
  { img: IMG.d2, name: "Dr. Marcus Reed", spec: "Internal Medicine" },
  { img: IMG.d3, name: "Dr. Aisha Patel", spec: "Pediatrics" },
  { img: IMG.d4, name: "Dr. Dana Nguyen", spec: "Cardiology" },
];
const Cap = ({ children }) => <span className="text-xs tracking-[0.2em] uppercase font-bold" style={{ color: BLUE }}>{children}</span>;

export default function MedicalDemo() {
  useEffect(() => { window.scrollTo(0, 0); document.title = "Harborlight Medical Clinic (Demo) | WebsiteTech.pro"; }, []);

  return (
    <div className="min-h-screen" style={{ background: "#F8FAFF", color: INK, ...body }} data-testid="medical-demo">
      <Link to="/demos" className="block w-full text-center text-[11px] tracking-widest uppercase py-1.5" style={{ background: "#0b1220", color: "#F9FAFB" }}>
        WebsiteTech.pro Demo · Healthcare — click to view all industry demos
      </Link>
      {/* utility bar */}
      <div className="hidden sm:block text-white text-xs" style={{ background: INK }}>
        <div className="max-w-7xl mx-auto px-6 h-9 flex items-center justify-between">
          <span className="flex items-center gap-2 opacity-90"><Phone size={12} /> (206) 555-0188 · Mon–Fri 8–6, Sat 8–2</span>
          <span className="opacity-90 hover:opacity-100 cursor-pointer">Patient Portal →</span>
        </div>
      </div>

      <DemoTopNav
        testid="medical-nav"
        logo={<div className="flex items-center gap-2"><span className="grid h-8 w-8 place-items-center rounded-lg text-white" style={{ background: BLUE }}><HeartPulse size={17} /></span><span className="font-extrabold tracking-tight text-lg" style={{ color: INK }}>Harborlight</span></div>}
        items={["Services", "Providers", "Patients", "About", "Contact"]}
        ctaLabel="Request Appointment"
        theme={{ bg: "rgba(248,250,255,0.9)", text: INK, accent: CORAL, accentText: "#fff", border: "#dbe4ff" }}
      />

      {/* hero */}
      <header data-testid="medical-hero" className="max-w-7xl mx-auto px-6 py-14 md:py-20 grid lg:grid-cols-2 gap-12 items-center">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}>
          <Cap>Welcoming New Patients</Cap>
          <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05]">Care that listens.<br /><span style={{ color: BLUE }}>Medicine you can trust.</span></h1>
          <p className="mt-5 text-lg text-slate-600 max-w-lg">Compassionate primary and specialty care for every stage of life — with same-day appointments and a team that treats you like family.</p>
          <div className="mt-8 flex flex-wrap gap-3">
            <button data-testid="medical-appointment-btn" className="rounded-xl px-6 py-3.5 text-sm font-semibold text-white flex items-center gap-2" style={{ background: CORAL }}>Request an Appointment <ArrowRight size={15} /></button>
            <button className="rounded-xl px-6 py-3.5 text-sm font-semibold" style={{ border: `1px solid ${BLUE}`, color: BLUE }}>Find a Provider</button>
          </div>
          <div className="mt-7 flex flex-wrap gap-5 text-sm text-slate-500">
            <span className="flex items-center gap-1.5"><ShieldCheck size={16} style={{ color: BLUE }} /> Most insurance accepted</span>
            <span className="flex items-center gap-1.5"><Clock size={16} style={{ color: BLUE }} /> Same-day visits</span>
          </div>
        </motion.div>
        <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8 }} className="relative">
          <img src={IMG.hero} alt="Harborlight clinic" className="rounded-3xl w-full h-[420px] object-cover shadow-xl" />
          <div className="absolute -bottom-5 -left-5 bg-white rounded-2xl shadow-lg p-4 flex items-center gap-3 border border-blue-50">
            <span className="grid h-10 w-10 place-items-center rounded-full text-white" style={{ background: BLUE }}><CalendarCheck size={18} /></span>
            <div><div className="font-bold text-sm">Same-day care</div><div className="text-xs text-slate-500">Book in under 2 minutes</div></div>
          </div>
        </motion.div>
      </header>

      {/* care services */}
      <section data-testid="medical-services" className="max-w-7xl mx-auto px-6 py-16">
        <div className="text-center"><Cap>Our Care Services</Cap><h2 className="mt-3 text-3xl md:text-4xl font-extrabold tracking-tight">Everything your family needs</h2></div>
        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {SERVICES.map((s, i) => (
            <motion.div key={s.name} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: (i % 3) * 0.08, duration: 0.5 }}
              className="rounded-2xl bg-white p-6 border border-blue-50 hover:shadow-lg transition-shadow" data-testid={`medical-service-${i}`}>
              <span className="grid h-11 w-11 place-items-center rounded-xl" style={{ background: "#EFF6FF", color: BLUE }}><s.icon size={20} /></span>
              <h3 className="mt-4 font-bold text-lg">{s.name}</h3>
              <p className="mt-1.5 text-sm text-slate-500">{s.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* providers */}
      <section data-testid="medical-providers" className="border-y py-16" style={{ background: "#EFF4FF", borderColor: "#dbe4ff" }}>
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center"><Cap>Meet Our Providers</Cap><h2 className="mt-3 text-3xl md:text-4xl font-extrabold tracking-tight">Doctors who know you by name</h2></div>
          <div className="mt-10 grid grid-cols-2 lg:grid-cols-4 gap-5">
            {PROVIDERS.map((p) => (
              <div key={p.name} className="bg-white rounded-2xl overflow-hidden border border-blue-50 text-center">
                <img src={p.img} alt={p.name} className="h-52 w-full object-cover" />
                <div className="p-4"><div className="font-bold">{p.name}</div><div className="text-xs" style={{ color: BLUE }}>{p.spec}</div></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* same-day band */}
      <section className="max-w-7xl mx-auto px-6 py-16">
        <div className="rounded-3xl p-8 md:p-12 text-white grid md:grid-cols-3 gap-8 items-center" style={{ background: BLUE }}>
          <div className="md:col-span-2">
            <h2 className="text-2xl md:text-3xl font-extrabold">Feeling unwell? See us today.</h2>
            <p className="mt-2 opacity-90">Same-day appointments for urgent, non-emergency needs. Insurance & billing questions welcome.</p>
          </div>
          <button className="rounded-xl px-6 py-3.5 text-sm font-semibold text-white justify-self-start md:justify-self-end" style={{ background: CORAL }}>Request an Appointment</button>
        </div>
      </section>

      {/* footer with disclaimer */}
      <footer data-testid="medical-footer" className="text-white" style={{ background: INK }}>
        <div className="max-w-7xl mx-auto px-6 py-12 grid md:grid-cols-4 gap-8 text-sm">
          <div><div className="font-extrabold text-lg">Harborlight Medical</div><p className="mt-3 opacity-70">Care that listens. Medicine you can trust.</p></div>
          <div><div className="uppercase tracking-widest text-xs opacity-80 mb-3">Care</div><p className="opacity-70 leading-7">Primary Care<br />Pediatrics<br />Women's Health</p></div>
          <div><div className="uppercase tracking-widest text-xs opacity-80 mb-3">Patients</div><p className="opacity-70 leading-7">Patient Portal<br />Insurance<br />Forms & Resources</p></div>
          <div><div className="uppercase tracking-widest text-xs opacity-80 mb-3">Contact</div><p className="opacity-70 leading-7">(206) 555-0188<br />hello@harborlight.demo</p></div>
        </div>
        <div data-testid="medical-disclaimer" className="border-t border-white/10">
          <div className="max-w-7xl mx-auto px-6 py-4 text-xs flex items-start gap-2" style={{ color: "#FCA5A5" }}>
            <AlertTriangle size={15} className="mt-0.5 shrink-0" />
            <span>This site and its AI assistant (Clara) provide general information only and are not medical advice, diagnosis, or treatment. In a medical emergency, call 911 immediately.</span>
          </div>
        </div>
      </footer>

      <AIConciergeWidget demo="medical" concierge={DEMO_THEMES.medical.concierge} />
    </div>
  );
}
